import React, { useContext } from 'react';
import { Button, Popconfirm, Space, Tag, Typography } from 'antd';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import PlanCard from '@totem/components/accountPlan/PlanCard';
import { Opportunity } from '@totem/components/accountPlan/types';
import {
  moneyExact,
  shortDate,
  STAGE_COLORS,
  STAGE_LABELS,
} from '@totem/components/accountPlan/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

const OpportunitiesCard = () => {
  const { data, openEditor, savePlan } = useContext(AccountPlanContext);
  if (!data) {
    return null;
  }
  const { plan } = data;
  const open = plan.opportunities.filter(
    (opportunity) =>
      opportunity.stage !== 'won' && opportunity.stage !== 'lost',
  );
  const pipeline = open.reduce(
    (sum, opportunity) => sum + (opportunity.amount ?? 0),
    0,
  );

  const remove = (id: string) =>
    savePlan({
      ...plan,
      opportunities: plan.opportunities.filter(
        (opportunity) => opportunity.id !== id,
      ),
    });

  const columns = [
    {
      title: 'Opportunity',
      dataIndex: 'title',
      render: (title: string, opportunity: Opportunity) => (
        <>
          <Typography.Text strong>{title}</Typography.Text>
          {opportunity.notes && (
            <div>
              <Typography.Text type="secondary">
                {opportunity.notes}
              </Typography.Text>
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right' as const,
      render: (amount?: number) =>
        amount === undefined || amount === null ? 'TBD' : moneyExact(amount),
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      render: (stage: string, opportunity: Opportunity) => (
        <>
          <Tag color={STAGE_COLORS[stage]}>{STAGE_LABELS[stage] ?? stage}</Tag>
          {opportunity.confidence > 0 && `${opportunity.confidence}%`}
        </>
      ),
    },
    {
      title: 'Close',
      dataIndex: 'closeDate',
      render: (date?: string) => shortDate(date),
    },
    { title: 'Owner', dataIndex: 'owner' },
    {
      title: '',
      dataIndex: 'id',
      render: (id: string) => (
        <Space size={0}>
          <Button
            type="link"
            size="small"
            onClick={() => openEditor({ section: 'opportunity', itemId: id })}
          >
            Edit
          </Button>
          <Popconfirm
            title="Remove this opportunity?"
            onConfirm={() => remove(id)}
          >
            <Button type="link" size="small" danger>
              Remove
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PlanCard
      variant="manual"
      onEdit={() => openEditor({ section: 'opportunity' })}
      title="Whitespace & Account-Team Opportunities"
      extra={
        <Space>
          <Typography.Text type="secondary">
            {open.length} open · {moneyExact(pipeline)} (AM-entered, not in
            NetSuite)
          </Typography.Text>
        </Space>
      }
    >
      <IBTable
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={plan.opportunities}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
        }}
        locale={{
          emptyText:
            'Nothing yet — track expansion ideas here before they become NetSuite opportunities.',
        }}
      />
    </PlanCard>
  );
};

export default OpportunitiesCard;
